import Logo from '../../../static/assets/images/logo-with-text-vertical.png'

/**
 * @description List of school / organization options. This list contains the general configuration settings for each schools subdomain deployment and is also used in several other
 * parts of the application such as the login page, the admin dashboard, and the magic link creation process. This is sorted alphabetically by the text field.
 *
 * @typedef {Object} SchoolOrganizationOption
 * @property {string[]} sso_methods - Methods for single sign-on (e.g., "saml", "openIdConnect","google", "email"). This determines the login method for the school.Use one if using saml or openId connect, use both if using google or email. Order Matters, first entry is primary
 * @property {string} logo - URL to the school's logo.
 * @property {string} domain - The school's login URL, this is where you will be directed after selecting a school from the wingspans.com school selection dropdown menu.
 * @property {string} key - Unique key for the school, used to match the subdomain of the current deployment.
 * @property {string} text - Display name of the school for the login page.
 * @property {string[]} staff_emails - List of staff specific email domains associated with the school, used to grant permissions to staff during SSO
 * @property {string[]} emails - List of email domains associated with the school that are allowed to sign up, if SSO does not match then the users account will not be created.
 * @property {string | null} custom_audience_label - Custom label for the audience type, used to display a specific message on the login page. If null, a default label will be used.
 * @property {string} value -  Unique key identifier for the school, federal school ID in most cases, may be the name of an org if not a school.
 * @property {("K-12" | "ADULT")} audience_type - Type of audience (e.g., "K-12", "ADULT"), used to distinguish lesson materials.
 * @property {string|null} saml_provider_id - SAML provider ID if applicable, otherwise null.
 * @property {string|null} openid_connect_provider_id - OpenID Connect provider ID if applicable, otherwise null.
 * @property {string|null} banner - URL to the login page banner image if any, otherwise null.
 * @property {string|null} banner_position - Horizontal position of the banner if any, otherwise null.
 * @property {("button" | "link" | null)} magic_link_type - Type of magic link if any, otherwise null.
 * @property {boolean} hide_name - Flag to determine if the schools name should be hidden in the signup page when their logo includes the name prominently.
 * @property {boolean} [hide_logo_on_home_page] - If true, the organization's logo is not displayed on the home page.
 * @property {boolean} reverse_names - Flag to determine if the users first and last names should be reversed upon account creation.
 * @property {string} state - State abbreviation for the school, used for default salaries when avaliable.
 * @property {string|null} petersons_institution_id - Petersons institution ID for colleges, used for major filtering .
 * @property {string} type - Either "school" or "organization". Used for wording decisions like "student" vs "user".
 * @property {string|null} zip_code - 5-digit ZIP code for the organization's primary location, used for default career data lookups.
 * @property {boolean} highlight_on_home_page: If true, organization's logo is displayed at top of home page.
 */
export const schoolOrganizationOptions = [
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FThe%20MET%20logo.jpeg?alt=media&token=2c1fbedb-fc27-4869-8fd7-1308bfbb4c98',
    domain: 'https://themet.wingspans.com',
    key: 'themet',
    zip_code: '02905',
    text: 'The MET High School',
    staff_emails: [],
    emails: ['@metmail.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '400166',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['openIdConnect'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fbunker-hill-logo-with-text.webp?alt=media&token=4f0499da-d9c6-40ec-9578-64585f408dae',
    domain: 'https://bunkerhill.wingspans.com',
    key: 'bunkerhill',
    zip_code: '02129',
    text: 'Bunker Hill Community College',
    staff_emails: [],
    emails: ['@bhcc.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '11210',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: 'oidc.bunkerhill',
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/sign-up-pages%2FBHCC%20Compressed.jpg?alt=media&token=28aa6eb8-5f33-4dd9-bfe5-f11488ef1fde',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'MA',
    petersons_institution_id: '5475',
    type: 'school',
    abbreviation: null,
    highlight_on_home_page: true,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fnorthland-pioneer-logo-with-text.jpg?alt=media&token=c48c90d0-8fbe-44a5-a5b3-6e42ec99031c',
    domain: 'https://northlandpioneer.wingspans.com',
    key: 'northlandpioneer',
    zip_code: '86025',
    text: 'Northland Pioneer Community College',
    staff_emails: ['@npc.edu'],
    emails: ['@npc.edu', '@mail.npc.edu', '@stu.npc.edu'],
    custom_audience_label: null,
    value: '011862',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.northlandpioneer',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/sign-up-pages%2FNPC%20963kB%20Compressed.jpg?alt=media&token=4e4094b4-de94-4d12-884d-f350d51350b3',
    banner_position: '50%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'AZ',
    petersons_institution_id: '7851',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRCC.jpg?alt=media&token=0e41f6e2-7ba4-4db4-975c-245bf6a27724',
    domain: 'https://roxbury.wingspans.com',
    key: 'roxbury',
    zip_code: '02120',
    text: 'Roxbury Community College',
    staff_emails: ['@rcc.mass.edu'],
    emails: ['@rcc.mass.edu', '@roxbury.edu'],
    custom_audience_label: null,
    value: '011930',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.roxbury',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/sign-up-pages%2FBetter%20Roxbury%20compressed.jpg?alt=media&token=47c2f5f5-a23f-4e95-b676-9d23094be448',
    banner_position: '50%',
    magic_link_type: 'link',
    hide_name: false,
    reverse_names: false,
    state: 'MA',
    petersons_institution_id: '8348',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNTU%20logo.jpeg?alt=media&token=ca4f6947-efa0-45f6-a8b2-6b9f9a9f0d79',
    domain: 'https://navajotech.wingspans.com',
    key: 'navajotech',
    zip_code: '87313',
    text: 'Navajo Tech University',
    staff_emails: [],
    emails: ['@navajotech.edu'],
    custom_audience_label: null,
    value: '016119',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'NM',
    petersons_institution_id: '140373',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FEagle-Rock-School-Press-Release-Logo-white-on-purple.jpeg?alt=media&token=fdb6bc1a-e1d4-4eb2-9413-bf4ed08865d4',
    domain: null,
    key: 'eaglerock',
    zip_code: '90041',
    text: 'Eagle Rock High School',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '060551',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCFCC%20.jpeg?alt=media&token=5e8a0041-1132-48d5-a433-bdfad5c66119',
    domain: 'https://capefear.wingspans.com',
    key: 'capefear',
    zip_code: '28401',
    text: 'Cape Fear Community College',
    staff_emails: [],
    emails: ['@cfcc.edu', '@mail.cfcc.edu', '@AD.cfcc.edu', '@ad.cfcc.edu'],
    custom_audience_label: null,
    value: '005320',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.capefear',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCape%20Fear%20Campus.jpeg?alt=media&token=85e75cdc-52cc-4df0-a54a-d540c96c232e',
    banner_position: '50%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'NC',
    petersons_institution_id: '5547',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['microsoft', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCCAC%20logo.jpeg?alt=media&token=f658b4ad-75bb-462a-a67a-46c6ffc0c189',
    domain: null,
    key: 'alleghenycounty',
    zip_code: '15233',
    text: 'Community College of Allegheny County',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '003231',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'PA',
    petersons_institution_id: '5914',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMillis%20High%20School%20logo.png?alt=media&token=f0c6ce7c-4ebc-4548-9338-fa06b72e5cf2',
    domain: null,
    key: 'millishighschool',
    zip_code: '02054',
    text: 'Millis High School',
    staff_emails: [],
    emails: ['@millisschools.org'],
    custom_audience_label: null,
    value: '01870020',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'MA',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  // {
  //   sso_methods: ["google"],
  //   logo: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FSomeville%20High%20School%20S%20Logo%202022-no%20circle%20SMALLER.jpeg?alt=media&token=3b7b69cd-0d86-49c7-bcde-1bd47eec6bc3",
  //   domain: "https://somerville.wingspans.com",
  //   key: "somerville",
  //   text: "Somerville Public Schools",
  //   staff_emails: [],
  //  emails: ["@k12.somerville.ma.us"],
  //   custom_audience_label: null,
  //   value: "251089001745",
  //   audience_type: "K-12",
  //   saml_provider_id: null,
  //   openid_connect_provider_id: null,
  //   banner:
  //     "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/sign-up-pages%2Fsomerville.jpeg?alt=media&token=579497c7-2cf5-4ac6-8ddf-c3697ac8dd7b",
  //   banner_position: "50%",
  //   magic_link_type: null,
  //   hide_name: false,
  //   reverse_names: false,
  //   state: "MA",
  //   petersons_institution_id: null,
  //   type: "school",
  // },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fcharette-hs.png?alt=media&token=f3160f6f-d39f-4de1-a490-f013ee503953',
    domain: 'https://charette.wingspans.com',
    key: 'charette',
    zip_code: '02903',
    text: 'Charette High School',
    staff_emails: [],
    emails: ['@charettepvd.org'],
    custom_audience_label: null,
    value: '81601',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: null,
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FURI-Ram.jpg?alt=media&token=653470b4-6c46-40dd-afc3-c7b029776f46',
    domain: 'https://uri.wingspans.com',
    key: 'uri',
    zip_code: '02881',
    text: 'University of Rhode Island',
    staff_emails: [],
    emails: ['@uri.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '003414',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.uri',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FURI.jpeg?alt=media&token=930a2f63-9d71-4d30-ba06-2ab8d2392abf',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: '9362',
    type: 'school',
    abbreviation: 'URI',
  },
  // {
  //   sso_methods: ["saml"],
  //   logo: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FKennedy%20King%20College%20GCR%20image.png?alt=media&token=876224f5-6409-470f-a9dd-0352eb82fb71",
  //   domain: "https://kennedyking.wingspans.com",
  //   key: "kennedyking",
  //   text: "Kennedy King College",
  //   staff_emails: [],
  // emails: ["@ccc.edu", "@student.ccc.edu"],
  //   custom_audience_label: null,
  //   value: "001654",
  //   audience_type: "ADULT",
  //   saml_provider_id: "saml.kennedyking",
  //   openid_connect_provider_id: null,
  //   banner:
  //     "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fkennedy%20king%20college%20campus-sm.jpeg?alt=media&token=508d2e1b-939c-421a-b524-5cc3ea9d1e92",
  //   banner_position: "70%",
  //   magic_link_type: null,
  //   hide_name: true,
  //   reverse_names: false,
  //   state: "IL",
  //   petersons_institution_id: "5706",
  //   type: "school",
  // },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FGeorgetown_Hoyas_Logo_.png?alt=media&token=a0d64fcb-89b5-4f6e-9fc2-4b8c4935aad0',
    domain: 'https://georgetown.wingspans.com',
    key: 'georgetown',
    zip_code: '20057',
    text: 'Georgetown University',
    staff_emails: [],
    emails: ['@georgetown.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '001445',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.georgetown',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FGeorgetown%20University%20campus.jpeg?alt=media&token=cadd4898-4cd8-428a-929a-87fe729363e2',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'DC',
    petersons_institution_id: '6446',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMendocino%20College%20logo.png?alt=media&token=4199ad9c-56e3-4131-bb16-16607794c799',
    domain: 'https://mendocino.wingspans.com',
    key: 'mendocino',
    zip_code: '95482',
    text: 'Mendocino College',
    staff_emails: ['@mendocino.edu'],
    emails: ['@mendocino.edu', '@student.mendocino.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '011672',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.mendocino',
    openid_connect_provider_id: 'oidc.mendocino',
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMendocino%20College%20campus.jpg?alt=media&token=7dfb10dc-ce65-4043-8e07-4f76213612bb',
    banner_position: '40%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '7404',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Froane%20state%20logo.png?alt=media&token=dcc0b8d4-f4af-4c46-959c-bf3664330360',
    domain: 'https://roanestate.wingspans.com',
    key: 'roanestate',
    zip_code: '37748',
    text: 'Roane State Community College',
    staff_emails: [],
    emails: ['@roanestate.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '009914',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.roanestate',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRoane%20State%20Community%20College.jpeg?alt=media&token=06d752e9-971c-451e-8468-dd90fbe49e8a',
    banner_position: '10%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: true,
    state: 'TN',
    petersons_institution_id: '8316',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fcollege-unbound-new-logo.webp?alt=media&token=20ca3a3d-bf7c-4189-a12a-a35fd4b82b1a',
    domain: 'https://collegeunbound.wingspans.com',
    key: 'collegeunbound',
    zip_code: '02903',
    text: 'College Unbound',
    staff_emails: [],
    emails: ['@collegeunbound.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '042836',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCollege%20Unbound%20campus.jpeg?alt=media&token=eec063a6-2810-4867-b102-ea3e93ce9f95',
    banner_position: '65%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: '100',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRIRAL%2BLogo%2B(white).jpg?alt=media&token=0235f492-a811-42c7-b651-2677999946fe',
    domain: 'https://riral.wingspans.com',
    key: 'riral',
    zip_code: '02895',
    text: 'Rhode Island Regional Adult Learning',
    staff_emails: ['@riral.org'],
    emails: ['@riral.org', '@student.riral.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '9001',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRIRAL%20-%20Woonsocket.jpg?alt=media&token=96e137c2-a241-4c5f-bb79-eb3e6797b1f0',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'organization',
    abbreviation: null,
  },
  {
    sso_methods: ['openIdConnect'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FParkland%20college%20logo.jpeg?alt=media&token=2a2db49e-0a71-4c56-a65c-c4f99dfd8f99',
    domain: 'https://parkland.wingspans.com',
    key: 'parkland',
    zip_code: '61821',
    text: 'Parkland College',
    staff_emails: ['@parkland.edu'],
    emails: ['@stu.parkland.edu', '@parkland.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '007118',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: 'oidc.parkland',
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FParklandCollege-ZoomBkgd_Campus-Daylight3.jpg?alt=media&token=0218ced9-321b-46ca-9a69-c7a993a588d9',
    banner_position: '88%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '8058',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fedmonds-college-logo-cropped.png?alt=media&token=432975e2-d1f8-4668-a9da-13d5571b8d75',
    domain: 'https://edmonds.wingspans.com',
    key: 'edmonds',
    zip_code: '98036',
    text: 'Edmonds College',
    staff_emails: ['@edmonds.edu'],
    emails: ['@edmonds.edu', '@student.edmonds.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '005501',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FEdmonds%20College.jpeg?alt=media&token=52972876-16ab-4542-a326-608249b0f223&_gl=1*99kj1v*_ga*MTI1ODQzNDQyNi4xNjYxOTU5MjY0*_ga_CW55HF8NVT*MTY5NTg1MTE4NS4xMjYuMS4xNjk1ODUxMjc3LjQzLjAuMA..',
    banner_position: '20%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'WA',
    petersons_institution_id: '6222',
    type: 'school',
    abbreviation: null,
  },
  // {
  //   sso_methods: [],
  //   logo: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMassHire%20-%20logo.png?alt=media&token=4d7ffaa3-f010-4464-a5e8-9256562d410f",
  //   domain: "https://masshiredowntown.wingspans.com",
  //   key: "masshiredowntown",
  //   text: "MassHire Career Center",
  //   staff_emails: [],
  // emails: [],
  //   audience: "",
  //   value: "masshiredowntown",
  //   audience_type: "ADULT",
  //   saml_provider_id: null,
  //   openid_connect_provider_id: null,
  //   banner:
  //     "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fmasshire-5-1000.jpg?alt=media&token=06d9ba0f-a193-478e-90c3-ff2a3361117c",
  //   banner_position: "40%",
  //   magic_link_type: "button",
  //   hide_name: true,
  //   reverse_names: false,
  //   state: "MA",
  //   petersons_institution_id: null,
  // },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FIvy%20Tech%20-%20Logo.png?alt=media&token=b9205138-f61f-40aa-bf62-04db28715475',
    domain: 'https://ivytech.wingspans.com',
    key: 'ivytech',
    zip_code: '46208',
    text: 'Ivy Tech Community College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '009917',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FIvy%20Tech%20-%20Campus.jpg?alt=media&token=4ea1df5f-16bb-4861-882d-eb94c036eb4d',
    banner_position: '20%',
    magic_link_type: 'button',
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'IN',
    petersons_institution_id: '6749',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPasadena%20City%20College%20logo.png?alt=media&token=a51bbb86-1143-40a1-a192-951ff59cadc7',
    domain: 'https://pasadena.wingspans.com',
    key: 'pasadena',
    zip_code: '91106',
    text: 'Pasadena City College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '1261',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPasadena%20City%20College.jpg?alt=media&token=34eb6436-680f-421e-be63-fba05146186b',
    banner_position: '20%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '8064',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fmohawk-valley-logo-400.png?alt=media&token=fefb3594-e270-4a8d-aced-5da05b5df3b3',
    domain: 'https://mohawkvalley.wingspans.com',
    key: 'mohawkvalley',
    zip_code: '13501',
    text: 'Mohawk Valley Community College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '002871',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMohawk%20Valley%20Community%20College%20-%20Campus.jpeg?alt=media&token=cba124b6-9669-4315-b29b-54d39443d379',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'NY',
    petersons_institution_id: '7545',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FWashburn%20University%20-%20Logo%202.png?alt=media&token=5353fd92-9627-4d23-bc9a-e3787fde46a4',
    domain: 'https://washburn.wingspans.com',
    key: 'washburn',
    zip_code: '66621',
    text: 'Washburn University',
    staff_emails: [],
    emails: ['@washburn.edu'],
    custom_audience_label: null,
    value: '001949',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.washburn',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FWashburn%20Univeristy%20-%20campus.jpg?alt=media&token=eb40c5fb-27c6-4390-94c8-470b58ce5543',
    banner_position: '60%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'KS',
    petersons_institution_id: '9577',
    type: 'school',
    abbreviation: null,
    hide_logo_on_home_page: true,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRio%20Salado%20College%20-%20Logo%202.jpeg?alt=media&token=e37d11fa-8ce8-4807-ba20-04877d8e6377',
    domain: 'https://riosalado.wingspans.com',
    key: 'riosalado',
    zip_code: '85281',
    text: 'Rio Salado College',
    staff_emails: ['@riosalado.edu', '@maricopa.edu'],
    emails: ['@riosalado.edu', '@domail.maricopa.edu', '@wingspans.com', '@maricopa.edu'],
    custom_audience_label: null,
    value: '014483',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.riosalado',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRio%20Salado%20Campus.jpeg?alt=media&token=95de3fbd-6e3e-4c0a-b957-ce1a72d034a2',
    banner_position: '60%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'AZ',
    petersons_institution_id: '8312',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FColumbus%20State%20-%20Logo.png?alt=media&token=5deedfda-9740-4a6e-a026-698e490fa682',
    domain: 'https://columbusstate.wingspans.com',
    key: 'columbusstate',
    zip_code: '31907',
    text: 'Columbus State College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '001561',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FColumbus%20State%20CC.jpg?alt=media&token=8ef2f19c-526e-4e0b-ba4f-b1429b5901d8',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'GA',
    petersons_institution_id: '5905',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCommunity%20College%20of%20Philadelphia%20-%20Logo.jpeg?alt=media&token=e1d30854-7310-4101-966a-8c44dade9c1f',
    domain: 'https://ccp.wingspans.com',
    key: 'ccp',
    zip_code: '19130',
    text: 'Community College of Philadelphia',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '003249',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCommunity%20College%20of%20Philadelphia%20-%20Campus.jpeg?alt=media&token=6ad8eb22-e874-4943-b904-b39f9bd7ed38',
    banner_position: null,
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'PA',
    petersons_institution_id: '5929',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNormandale%20Community%20College%20-%20Logo.jpeg?alt=media&token=35ecc71c-0b4f-43bb-86d3-cc02740e6e40',
    domain: 'https://normandale.wingspans.com',
    key: 'normandale',
    zip_code: '55431',
    text: 'Normandale Community College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '007954',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNormandale%20Community%20College%20-%20Campus.jpg?alt=media&token=7895a1c0-aabc-41df-8c76-a08ed9cf7280',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'MN',
    petersons_institution_id: '7784',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPortland%20Community%20College%20-%20Logo.jpg?alt=media&token=78efac6b-710d-466b-ac4c-7baa0ee2f400',
    domain: 'https://portland.wingspans.com',
    key: 'portland',
    zip_code: '97219',
    text: 'Portland Community College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '003213',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPortland%20CC.jpeg?alt=media&token=aa223fe3-3526-4f39-bf27-3858e290e2a3',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'OR',
    petersons_institution_id: '8185',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FHighline%20College%20-%20Logo.png?alt=media&token=8e3176b6-72fe-4244-a0fc-e45c25a35e8e',
    domain: 'https://highline.wingspans.com',
    key: 'highline',
    zip_code: '98198',
    text: 'Highline College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '003781',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FHighline%20College%20-%20Campus.jpeg?alt=media&token=926e0183-faa6-4cc7-bb3c-ee80fa691ef4',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'WA',
    petersons_institution_id: '6635',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fmadison-logo-with-text.jpg?alt=media&token=b8790455-d5e8-4c52-8623-62b19866928b',
    domain: 'https://madisoncollege.wingspans.com',
    key: 'madisoncollege',
    zip_code: '53704',
    text: 'Madison College',
    staff_emails: [],
    emails: ['@madisoncollege.edu'],
    custom_audience_label: null,
    value: '4007',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.madison',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMadison%20College%20-%20Campus.jpeg?alt=media&token=1193dca6-f1bd-4928-ac55-35d261caa0b4',
    banner_position: '60%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: true,
    state: 'WI',
    petersons_institution_id: '7278',
    type: 'school',
    abbreviation: null,
    highlight_on_home_page: true,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fpolaris_MEP_logo-sm.jpg?alt=media&token=d65cff7c-f847-42d2-b8b4-a9c84d8c3200',
    domain: 'https://polarismep.wingspans.com',
    key: 'polarismep',
    zip_code: '02903',
    text: 'Polaris MEP',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: 'polarismep',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F072_PolarisMEP_background-sm.jpg?alt=media&token=c0baf877-574b-4f30-8812-eadbc939f589',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'organization',
    abbreviation: null,
  },
  {
    sso_methods: ['google', 'email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FWesterly%20Public%20Schools%20logo.jpeg?alt=media&token=c8a2e14b-fedc-4339-9988-1b3ef40f9546',
    domain: 'https://westerly.wingspans.com',
    key: 'westerly',
    zip_code: '02891',
    text: 'Westerly School District',
    staff_emails: ['@wpsri.org'],
    emails: ['@westerly.k12.ri.us', '@wpsri.org', '@students.westerlyps.org', '@students.wpsri.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '440117000325',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FWesterly%20Public%20Schools.jpeg?alt=media&token=14cae313-58b9-495c-8348-40e2eff2ee85',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Flubbock2.png?alt=media&token=f1752e4b-d89d-431f-983f-61e2208d6331',
    domain: 'https://lubbock.wingspans.com',
    key: 'lubbock',
    zip_code: '79401',
    text: 'Lubbock ISD',
    staff_emails: ['@lubbockisd.org'],
    emails: ['@lubbockisd.org', '@lubbockisd.net', '@wingspans.com'],
    custom_audience_label: null,
    value: '4828500',
    audience_type: 'K-12',
    saml_provider_id: 'saml.lubbock',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Flubbock-statue.jpg?alt=media&token=5d12ba39-7f43-4624-ad16-b17523e6504b',
    banner_position: '40%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'TX',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fdallas3.jpg?alt=media&token=a7e5348b-1767-4bab-9a8e-cff6d32ca9ca',
    domain: 'https://dallaswfs.wingspans.com',
    key: 'dallaswfs',
    zip_code: '75201',
    text: 'Dallas Workforce Solutions',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: 'wfsdallas',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fdallas.jpg?alt=media&token=459fde92-2bcb-47bc-9b29-551617e8b884',
    banner_position: '60%',
    magic_link_type: 'button',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'TX',
    petersons_institution_id: null,
    type: 'organization',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fwingspanslogo.png?alt=media&token=db601cc2-f784-4e04-9f5a-985bed9da723',
    domain: 'https://team.wingspans.com',
    key: 'team',
    zip_code: '02903',
    text: 'Wingspans Team',
    staff_emails: [],
    emails: ['@wingspans.com'],
    custom_audience_label: 'Wingspans Team',
    value: '000000',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.wingspans',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fjuan-ramos-EWN0rrwbBIQ-unsplash%20(1).jpg?alt=media&token=7862983b-2666-4583-9310-d76c87f2f667',
    banner_position: '50%',
    magic_link_type: 'button',
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    custom_audience_label: 'Wingspans Team',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    // use google for now, remove saml once confirmed
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FShawnee%20logo.png?alt=media&token=933bf33e-a268-4dd7-b263-711dfd48cff9',
    domain: 'https://shawnee.wingspans.com',
    key: 'shawnee',
    zip_code: '62992',
    text: 'Shawnee Community College',
    staff_emails: [],
    emails: ['@shawneecc.edu'],
    custom_audience_label: null,
    value: '007693',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.shawnee',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FShawnee%20campus.jpg?alt=media&token=7eee7ee8-ebbd-4348-ba5b-3f7ae4234e34',
    banner_position: '50%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '8616',
    type: 'school',
    abbreviation: null,
  },
  {
    // k-12 demo school with no SSO
    sso_methods: ['email'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fwingspanslogo.png?alt=media&token=db601cc2-f784-4e04-9f5a-985bed9da723',
    domain: 'https://k12.wingspans.com',
    key: 'k12',
    zip_code: '02903',
    text: 'K-12 demo',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: 'k12',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fkids%20copy%20(1).gif?alt=media&token=fab7a71a-56fa-4537-b867-2dbb61d520d2',
    banner_position: '55%',
    magic_link_type: null,
    hide_name: false,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    // adult demo school with petersons ID set to mendocino
    sso_methods: ['email'],
    logo: Logo,
    domain: 'https://demo.wingspans.com',
    key: 'demo',
    zip_code: '93454',
    text: 'Demo School',
    staff_emails: [],
    emails: ['@wingspans.com'],
    custom_audience_label: null,
    value: 'demo',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: '25%',
    magic_link_type: 'link',
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '5052',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRIC_Logo_Square_Burgundy_RGB%20(1).png?alt=media&token=21627a20-65fd-4910-a301-5fcf09ce082a',
    domain: 'https://ric.wingspans.com',
    key: 'ric',
    zip_code: '02908',
    text: 'Rhode Island College',
    staff_emails: ['@ric.edu'],
    emails: ['@email.ric.edu', '@ric.edu'],
    custom_audience_label: null,
    value: '003407',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.ric',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FRIC%20Forman_Center%20(1)%20(4)%20(1).jpg?alt=media&token=11629c4c-623f-4d1e-bfa9-6fa527f4c14c',
    banner_position: '75%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: '8292',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FOCC%20Logo%20Black%20(1).png?alt=media&token=e769c9a4-d16e-48f2-a7a1-2b2382ebba93',
    domain: 'https://onondaga.wingspans.com',
    key: 'onondaga',
    zip_code: '13215',
    text: 'Onondaga Community College',
    staff_emails: ['@sunyocc.edu'],
    emails: ['@sunyocc.edu', '@clasnet.sunyocc.edu'],
    custom_audience_label: null,
    value: '160973001',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.onondaga',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FOnondaga%20CC.jpg?alt=media&token=fcfd050c-b5a5-411d-a1fe-593c847797e5',
    banner_position: '40%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'NY',
    petersons_institution_id: '7980',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FOKSTATE%20logo%20(1).png?alt=media&token=46aef9f5-c482-41a0-ac13-2a9913824449',
    domain: 'https://osu-okc.wingspans.com',
    key: 'osu-okc',
    zip_code: '73107',
    text: 'OSU OKC',
    staff_emails: [],
    emails: ['@okstate.edu'],
    custom_audience_label: null,
    value: '009647',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.osu-okc',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FOSUOKC%20Building%205%20(1).jpg?alt=media&token=91fe4800-2dd4-4c8a-b343-9b38fc1f8511',
    banner_position: '40%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'OK',
    petersons_institution_id: '7969',
    type: 'school',
    abbreviation: 'OSU-OKC',
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FAllan%20Hancock%20College%20-%20Black%20(1).jpg?alt=media&token=0ea0fbe1-c086-49b2-98b0-28a176093c24',
    domain: 'https://hancock.wingspans.com',
    key: 'hancock',
    zip_code: '93454',
    text: 'Allan Hancock',
    staff_emails: ['@hancockcollege.edu'],
    emails: ['@hancockcollege.edu', '@my.hancockcollege.edu'],
    custom_audience_label: null,
    value: '3143396',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.hancock',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FAllan%20Hancock%20Student%20Services%20DSC_1595%20(1).jpg?alt=media&token=5d6de5ef-958b-4feb-a285-fd43d7d9c0d8',
    banner_position: '40%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '5052',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPurpleWolf-Web.png?alt=media&token=5b019218-84df-4aa6-9585-0d1967bbcfe8',
    domain: 'https://fortbragg.wingspans.com',
    key: 'fortbragg',
    zip_code: '95437',
    text: 'Fort Bragg',
    staff_emails: [],
    emails: ['@fbusd.us', '@wingspans.com'],
    custom_audience_label: null,
    value: '061407001610',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Ffort-bragg-high-school-1024x683.jpg?alt=media&token=ff19b686-7999-4a90-a031-18e33416138a',
    banner_position: '70%',
    magic_link_type: false,
    hide_name: false,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNoyo%20Logo.png?alt=media&token=302f8ea2-0844-4db3-a393-110ac56b2e78',
    domain: 'https://noyo.wingspans.com',
    key: 'noyo',
    zip_code: '95437',
    text: 'Noyo',
    staff_emails: [],
    emails: ['@fbusd.us', '@wingspans.com'],
    custom_audience_label: null,
    value: '061407000660',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FIMG_9342.JPG?alt=media&token=16ed281e-92d4-41f9-b3ed-455ca101d18f',
    banner_position: '70%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: 'Noyo',
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fcalbright-logo-cropped.webp?alt=media&token=826a8ca1-93d5-4649-a18b-31e964775428',
    domain: 'https://calbright.wingspans.com',
    key: 'calbright',
    zip_code: '95811',
    text: 'Calbright College',
    staff_emails: ['@calbright.edu', '@calbright.org'],
    emails: ['@calbright.org', '@calbrightcollege.org', '@calbrightcollege.edu', '@calbright.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '83-2103700',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fshutterstock_2448871857.jpg?alt=media&token=533259bd-f555-4ea8-b628-6e2a9c04c757',
    banner_position: '70%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '101',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fcochise-logo1.png?alt=media&token=281d6e56-1429-466e-9e77-eb065cfb3c92',
    domain: 'https://cochise.wingspans.com',
    key: 'cochise',
    zip_code: '85607',
    text: 'Cochise College',
    staff_emails: [],
    emails: ['@students.cochise.edu', '@email.cochise.edu', '@cochise.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '860183151',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.cochise',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCochise%20College.jpg?alt=media&token=b18d27a9-86cf-4517-a546-58acd1ae4052',
    banner_position: '30%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'AZ',
    petersons_institution_id: '5779',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fbeacon_logo_2015_color%20copy.jpeg?alt=media&token=34aa9a3a-2d43-4909-b623-725088e9292e',
    domain: 'https://beaconart.wingspans.com',
    key: 'beaconart',
    zip_code: '02895',
    text: 'Beacon Charter School',
    staff_emails: [],
    emails: ['@beaconart.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '000132505',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FBeacon%20Arts.jpeg?alt=media&token=3c62a057-5e8c-4951-896f-4ee53a39ee2e',
    banner_position: '70%',
    magic_link_type: false,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fyouthbuild.webp?alt=media&token=a63cd5a0-60bb-419d-951d-0d4e1a2006fe',
    domain: 'https://youthbuildpvd.wingspans.com/',
    key: 'youthbuildpvd',
    zip_code: '02907',
    text: 'YouthBuild Preparatory Academy',
    staff_emails: ['@ybprep.org'],
    emails: ['@ybprep.org', '@youthbuildprov.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '81-3957029',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FYBP%20Picture.JPG?alt=media&token=a7e2ee15-90eb-4561-a511-3d0dea8f27c2',
    banner_position: '15%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FUpperLakeHighSchoolPrimaryLogoImage.png?alt=media&token=598ac728-09f4-4baf-84b1-3ed3e0c455c3',
    domain: 'https://upperlake.wingspans.com/',
    key: 'upperlake',
    zip_code: '95485',
    text: 'Upper Lake Unified School District',
    staff_emails: [],
    emails: ['@ulusd.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '0601442',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F464955548_10220568265595192_2045133916885999108_n.jpg?alt=media&token=11609121-eafb-4129-ab5d-91af285814d1',
    banner_position: '50%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FLong%20Color-%20black%20red%20transparent%20(1)%20(1)%20(1).png?alt=media&token=81d191e7-00e8-43cc-8913-bcbb8569c22e',
    domain: 'https://everettcc.wingspans.com/',
    key: 'everettcc',
    zip_code: '98201',
    text: 'Everett Community College',
    staff_emails: ['@everettcc.edu'],
    emails: ['@students.everettcc.edu ', '@everettcc.edu', '@wingspans.com'],
    custom_audience_label: null,
    value: '003776',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fevcc_tulips%20(1).jpg?alt=media&token=b9c9a225-cb60-401e-8ebf-803a8891f495',
    banner_position: '50%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'WA',
    petersons_institution_id: '6296',
    type: 'school',
    abbreviation: 'EvCC',
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F24%20Sandburg-Logo-RGB%20(1).png?alt=media&token=f534e259-9ce7-4221-ae95-6fe94881fba2',
    domain: 'https://sandburg.wingspans.com',
    key: 'sandburg',
    zip_code: '61401',
    text: 'Carl Sandburg College',
    staff_emails: ['@sandburg.edu'],
    emails: ['@sandburg.edu', '@student.sandburg.edu'],
    custom_audience_label: null,
    value: '007265',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.sandburg',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FSandburg_Patio02.jpg?alt=media&token=e75f9217-e1ad-435e-91bd-d6743193261b',
    banner_position: '50%',
    magic_link_type: 'link',
    hide_name: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '5562',
    type: 'school',
    abbreviation: 'Sandburg',
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FMVCC%20logo%20full%20color%20(1).jpg?alt=media&token=34d6c2a6-ecba-4a9c-8ce9-2e157fa46c25',
    domain: 'https://morainevalley.wingspans.com',
    key: 'morainevalley',
    zip_code: '60465',
    text: 'Moraine Valley Community College',
    staff_emails: ['@morainevalley.edu'],
    emails: ['@morainevalley.edu', '@student.morainevalley.edu'],
    custom_audience_label: null,
    value: '007692',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.morainevalley',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FD771CAM027.jpg?alt=media&token=ce9353e2-470f-4278-9605-e468fc8a303a',
    banner_position: '50%',
    magic_link_type: false,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '7579',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FBC_Circle_125C.jpg?alt=media&token=b8f21a09-6a0e-4a3a-a329-5ae3eec62e09',
    domain: 'https://butte.wingspans.com',
    key: 'butte',
    zip_code: '95965',
    text: 'Butte College',
    staff_emails: ['@butte.edu'],
    emails: ['@butte.edu', '@student.butte.edu'],
    custom_audience_label: null,
    value: '006972',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.butte',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FBCcampus.jpg?alt=media&token=78c1c7a6-cf3a-49c2-9a10-7c9ff8f05b12',
    banner_position: '70%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '5481',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Flogo%20shield%20and%20name%20-%20TAPA%20%202018.jpg?alt=media&token=fa3cfc36-28d8-4242-a4de-d0d31bf6ae60',
    domain: 'https://tapa.wingspans.com',
    key: 'tapa',
    zip_code: '02903',
    text: 'Trinity Academy for the Performing Arts',
    staff_emails: [],
    emails: ['@tapaprovidence.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '27-2516469',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F2025%20Graduation%20Groups%20054.JPG?alt=media&token=69d26c7d-2622-49f1-9749-1cc0c7229a06',
    banner_position: null,
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: 'TAPA',
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Flogos%20-%20pilgrim%2C%20toll%20gate%2C%20vets%2C%20winman.zip%20-%201.png?alt=media&token=d1f18e40-02cc-427a-a616-83578e2ea692',
    domain: 'https://warwick.wingspans.com',
    key: 'warwick',
    zip_code: '02886',
    text: 'Warwick Schools',
    staff_emails: [],
    emails: ['@warwickschools.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '4401110',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: '25%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FIC_LogoBlue281TextBelow.jpg?alt=media&token=0a3ea879-378a-4c87-ab70-84ce5bffaa44',
    domain: 'https://iowacentral.wingspans.com',
    key: 'iowacentral',
    zip_code: '50501',
    text: 'Iowa Central Community College',
    staff_emails: ['@iowacentral.edu'],
    emails: ['@iowacentral.edu', '@tritons.iowacentral.edu', '@iccc.cc.ia.us'],
    custom_audience_label: null,
    value: '004597',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.iowacentral',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F20250523-0X4A8462%20(1).jpg?alt=media&token=956c2154-0986-4d9b-95e4-fb265fcf031b',
    banner_position: '20%',
    magic_link_type: false,
    hide_name: true,
    reverse_names: false,
    state: 'IA',
    petersons_institution_id: '6848',
    type: 'school',
    abbreviation: null,
    highlight_on_home_page: true,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNorth%20Smithfield.png?alt=media&token=92f1d4dc-8726-4e4d-b279-0f92b9c6f1d3',
    domain: 'https://northsmithfield.wingspans.com/',
    key: 'northsmithfield',
    zip_code: '02896',
    text: 'North Smithfield Schools',
    staff_emails: [],
    emails: ['@northsmithfieldschools.com', '@nsps.us', '@wingspans.com'],
    custom_audience_label: null,
    value: '440081000399',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FNorthSmithfieldBanner.png?alt=media&token=2d4a9caa-5509-4df3-8168-0984c6d8730d',
    banner_position: '25%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Ftimes2.jpg?alt=media&token=4dbce3d7-17ab-40b3-a578-52b5b9d38fca',
    domain: 'https://times2.wingspans.com',
    key: 'times2',
    zip_code: '02909',
    text: 'Times Squared Academy',
    staff_emails: [],
    emails: ['@times2.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '440090000530',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: null,
    banner_position: '25%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fsk%20logo.png?alt=media&token=ee51ab21-2d6f-4bd9-beda-4629e69e5ecd',
    domain: 'https://southkingstown.wingspans.com',
    key: 'southkingstown',
    zip_code: '02879',
    text: 'South Kingstown High School',
    staff_emails: ['@sksd-ri.net'],
    emails: ['@student.sksd-ri.net', '@sksd-ri.net', '@wingspans.com'],
    custom_audience_label: null,
    value: '440102000265',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fskhs%20school%20pic.jpg?alt=media&token=db2e5952-3588-48d7-bb3e-48f095ea082a',
    banner_position: '25%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['google'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPCS%20Logo%20(Blue%20Pantone%20300%20with%20Transparent%20Background).png?alt=media&token=7b109ae5-7794-4c96-8308-ceb6cdf2a361',
    domain: 'https://paulcuffee.wingspans.com',
    key: 'paulcuffee',
    zip_code: '02903',
    text: 'Paul Cuffee School',
    staff_emails: ['@paulcuffee.org'],
    emails: ['@paulcuffee.org', '@students.paulcuffee.org', '@wingspans.com'],
    custom_audience_label: null,
    value: '440003200526',
    audience_type: 'K-12',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FPaul_Cuffee_School%2C_Upper_School.jpg?alt=media&token=9000038c-a563-4fe5-9117-8de8ce9c46a7',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: null,
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fedo_horizontal_color.png?alt=media&token=a809e5e8-0d62-45c9-a84b-bc482a79dbe2',
    domain: 'https://longbeach.wingspans.com',
    key: 'longbeach',
    zip_code: '90806',
    text: 'Long Beach Workforce Innovation Network',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: 'longbeach',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FSkyline%202%5B74%5D%20(1).jpg?alt=media&token=ff666b1f-beb7-4f02-a516-eee6a6ed0339',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: null,
    type: 'organization',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fshasta_logo.jpg?alt=media&token=b76132cb-568c-48a1-95ee-ebbd368fe263',
    domain: 'https://shasta.wingspans.com',
    key: 'shasta',
    zip_code: '96003',
    text: 'Shasta College',
    staff_emails: ['@shastacollege.edu'],
    emails: ['@students.shastacollege.edu', '@shastacollege.edu'],
    custom_audience_label: null,
    value: '001289',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.shasta',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fshasta_wallpaper.jpg?alt=media&token=4d678d64-d24a-4458-9f80-7cb667ad90f9',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'CA',
    petersons_institution_id: '8615',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FIVCC_Main_%20Logo_Color%20(1).png?alt=media&token=07d3897b-fa15-4405-b9c0-e6c5550dbc59',
    domain: 'https://ivcc.wingspans.com',
    key: 'ivcc',
    zip_code: '61348',
    text: 'Illinois Valley Community College',
    staff_emails: [],
    emails: ['@ivcc.edu'],
    custom_audience_label: null,
    value: '001705',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.ivcc',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCampus-May20-34-min%20(1).jpg?alt=media&token=88d520af-acd9-409a-a5b9-f1593b37f9e7',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '6716',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fwetcc-logo-alt.svg?alt=media&token=7c404b62-b470-4fae-bbb3-685d65c1309c',
    domain: 'https://wetcc.wingspans.com',
    key: 'wetcc',
    zip_code: '56557',
    text: 'White Earth Tribal and Community College',
    staff_emails: [],
    emails: ['@wetcc.edu'],
    custom_audience_label: null,
    value: '039214',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.wetcc',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FWetccCampus.png?alt=media&token=efe7ff8e-e8e4-4d78-a18b-c192520aaed1',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'MN',
    petersons_institution_id: '168788',
    type: 'school',
    abbreviation: 'WETCC',
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fwsu-tech-logo-cropped.png?alt=media&token=1215a5b4-e77f-4496-ab29-79a85b317a16',
    domain: 'https://wsutech.wingspans.com',
    key: 'wsutech',
    zip_code: '67210',
    text: 'WSU Tech',
    staff_emails: [],
    emails: ['@wsutech.edu'],
    custom_audience_label: null,
    value: '005498',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.wsutech',
    openid_connect_provider_id: null,
    banner:
      'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fwsutech_cover.jpeg?alt=media&token=c6c18140-7a06-432b-be21-71ea2a39364b',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'KS',
    petersons_institution_id: '139890',
    type: 'school',
    abbreviation: null,
  },
    {
    sso_methods: ['saml'],
    logo: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fsuny-erie-header-logo.png?alt=media&token=b2346753-4648-4fe6-a52f-eaf269439bd1",
    domain: 'https://erie.wingspans.com',
    key: 'erie',
    zip_code: '14215',
    text: 'Erie Community College',
    staff_emails: [],
    emails: ['@ecc.edu', '@students.ecc.edu'],
    custom_audience_label: null,
    value: '010684',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.erie',
    openid_connect_provider_id: null,
    banner: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fsuny-erie.jpg?alt=media&token=376f2c1b-0480-4430-b74a-be7a758b7359",
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'NY',
    petersons_institution_id: '6273',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fccri.jpeg?alt=media&token=3a3470e5-2964-449e-ae44-de5223852f8b',
    domain: 'https://ccri.wingspans.com',
    key: 'ccri',
    zip_code: '02909',
    text: 'Community College of Rhode Island',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '004916',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCommunity_College_of_Rhode_Island_Knight_Campus.jpg?alt=media&token=aba0d920-ac47-4c1a-bdac-8507a61c1259",
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'RI',
    petersons_institution_id: '8294',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: [],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FCT-STATE-logo-color-RGB.png?alt=media&token=0b1f65e1-0a9e-4ac7-9c0a-0fea7c2a2d70',
    domain: 'https://ctstate.wingspans.com',
    key: 'ctstate',
    zip_code: '06103',
    text: 'CT State Community College',
    staff_emails: [],
    emails: [],
    custom_audience_label: null,
    value: '007635',
    audience_type: 'ADULT',
    saml_provider_id: null,
    openid_connect_provider_id: null,
    banner: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2FTunxis-Community-College-4ocy73fmye80oc8880osok8s8-1122.jpg?alt=media&token=d975f5db-a2b2-4a2a-955c-466c0d96893e',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    hide_logo_on_home_page: true,
    reverse_names: false,
    state: 'CT',
    petersons_institution_id: '282756',
    type: 'school',
    abbreviation: null,
  },
  {
    sso_methods: ['saml'],
    logo: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2Fbhcc-logo.jpeg?alt=media&token=c43fc131-4f0c-4947-be00-7050b36a2769',
    domain: 'https://bhc.wingspans.com',
    key: 'bhc',
    zip_code: '61265',
    text: 'Black Hawk College',
    staff_emails: ['@bhc.edu'],
    emails: ['@bhc.edu','@student.bhc.edu'],
    custom_audience_label: null,
    value: '001638',
    audience_type: 'ADULT',
    saml_provider_id: 'saml.bhc',
    openid_connect_provider_id: null,
    banner: 'https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/school-logos%2F30282-Greetings%20from%20BHC_Social%20Post_4x3_01%20(1).jpg?alt=media&token=b13b7583-7bba-4fcb-be09-7a3da509dbe1',
    banner_position: '15%',
    magic_link_type: null,
    hide_name: true,
    reverse_names: false,
    state: 'IL',
    petersons_institution_id: '5376',
    type: 'school',
    abbreviation: null,
  },
].sort(function (a, b) {
  return a.text === b.text ? 0 : a.text < b.text ? -1 : 1
})
const codeToSubdomain = new Map()
schoolOrganizationOptions.forEach((orgOptions) => {
  if (orgOptions.value) {
    codeToSubdomain.set(orgOptions.value, orgOptions)
  }
})
const subdomainToOrgOptions = new Map()
schoolOrganizationOptions.forEach((orgOptions) => {
  if (orgOptions.key) {
    subdomainToOrgOptions.set(orgOptions.key, orgOptions)
  }
})
export const getClientSubdomain = (code) => codeToSubdomain.get(code)?.key
export const getOrgOptions = (code) => codeToSubdomain.get(code)
export const getOrgOptionsFromSubdomain = (subdomain) => subdomainToOrgOptions.get(subdomain)
